<template>
  <div v-if="i18nReady" class="specialty-filter" :class="classModifiers">
    <sp-quantum-select
      ref="picker"
      :aria-label="label"
      class="specialty-filter__select"
      navigation="accordion"
      highlight-first-result
      focus-search-on-open
      selectable-branches
      :branch-toggle-label="t('specialty_toggle')"
      start-on-root
      :items.prop="models"
      :search="searchQuery"
      :selected="selected"
      :loading="loading"
      :location="location"
      :open="open"
      :pointer="!toBoolean(hidePointer)"
      :readonly="readonly"
      :close-on-select="closeOnSelect"
      :hide-search-bar="hideSearchBar"
      :hide-title-bar="hideTitleBar"
      :max-results="maxResults"
      :placeholder="t('specialty_search')"
      :input-test-id="TestId.finder.specialtyFilterInput"
      :item-test-id="TestId.finder.specialtyFilterOption"
      show-search-bar-in-sheet
      :no-data-text="t('no_results')"
      @update-search="handleUpdateSearch"
      @search-enter="handleSearchEnter"
      @update-selected="handleUpdateSelected"
      @update-open="handleUpdateOpen"
    >
      <FilterActivatorSlot
        :label="label"
        :text="selectedText"
        :prepend-icon="prependIcon"
        :append-icon="appendIcon"
        :open="open"
        @clear="clearSelectedModel"
      >
        <template v-if="$slots.activator" #activator="activatorProps">
          <slot name="activator" v-bind="activatorProps" />
        </template>
      </FilterActivatorSlot>
    </sp-quantum-select>
  </div>
</template>

<script setup>
import { computed, useTemplateRef, watch } from "vue";
import { t, i18nReady } from "#ce/shared/filters/i18n";
import { watchImmediate } from "@vueuse/core";
import { booleanAttribute, toBoolean, trimToUndefined } from "#ce/utils/props";
import { firstDetail, isComposing } from "#ce/utils/events";
import { TestId } from "#/test-ids";
import { useForm } from "#ce/composables/useForm";
import { useControllableOpen } from "#ce/shared/filters/composables/useControllableOpen";
import { useSpecialtyFilter } from "#ce/shared/filters/specialty/composables/useSpecialtyFilter";
import FilterActivatorSlot from "#ce/shared/filters/components/FilterActivatorSlot.ce.vue";

const emit = defineEmits(["update:model-value", "update:open", "select"]);

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Object, Array],
    default: undefined,
  },
  label: {
    type: String,
    default: "Specialty",
  },
  /**
   * The activator glyph. `service-filled` is the generic specialty/service icon
   * the common-specialty rows already use server-side.
   */
  prependIcon: {
    type: String,
    default: "service-filled",
  },
  appendIcon: {
    type: String,
    default: "popup-arrow-filled",
  },
  /**
   * The doctor-finder embed uuid, used to read the correct shared omnisearch
   * store instance (one fetch per embed). Defaults to the shared store.
   */
  uuid: {
    type: String,
    default: undefined,
  },
  /** @type {Boolean|String} */
  hideSearchBar: booleanAttribute(),
  /** @type {Boolean|String} */
  hideTitleBar: booleanAttribute(),
  /** @type {Boolean|String} */
  hidePointer: booleanAttribute(),
  /** @type {Boolean|String} */
  open: booleanAttribute(),
  location: {
    type: String,
    default: undefined,
  },
  /** @type {Boolean|String} */
  inline: booleanAttribute(),
  /** @type {Boolean|String} */
  closeOnSelect: booleanAttribute(),
  /** @type {Boolean|String} */
  readonly: booleanAttribute(),
  maxResults: {
    type: [Number, String],
    default: 50,
  },
});

defineExpose({ props });

const { mirrorFormValue } = useForm();

const store = useSpecialtyFilter({ uuid: trimToUndefined(props.uuid), modelValue: trimToUndefined(props.modelValue) });
const { models, loading, searchQuery, selectedValue, selectedText, selectedRenderValue } = store;

// The applied skill value drives both the form value and the consumer event.
const model = computed(() => selectedValue.value);

const selected = computed(() => (selectedRenderValue.value != null ? [selectedRenderValue.value] : []));

function emitModelValue(value) {
  if (value !== props.modelValue) {
    emit("update:model-value", value);
    emit("select", { value, text: selectedText.value });
  }
}

watchImmediate(model, emitModelValue);
// The host app dispatches the one change per user update.
mirrorFormValue(model);

// Keep the selection in sync when the value is set externally (URL param,
// session restore, the in-bar wrapper's clear button).
function syncSelectedFromModelValue(value) {
  const next = trimToUndefined(value);
  if (next !== selectedValue.value) {
    selectedValue.value = next;
  }
}

watch(() => props.modelValue, syncSelectedFromModelValue);

const open = useControllableOpen(() => props.open, emit);

function handleUpdateSearch(event) {
  searchQuery.value = firstDetail(event) ?? "";
}

const picker = useTemplateRef("picker");

function handleSearchEnter(event) {
  if (!isComposing(firstDetail(event))) {
    picker.value?.selectFirstResult?.({ markedOnly: true });
  }
}

function handleUpdateSelected(event) {
  const selection = firstDetail(event);
  const [value] = selection ?? [];
  selectedValue.value = value != null ? store.payloadValueForNode(value) : undefined;
}

function handleUpdateOpen(event) {
  open.value = firstDetail(event);
}

function clearSelectedModel() {
  selectedValue.value = undefined;
}

const classModifiers = computed(() => ({
  "--inline": toBoolean(props.inline),
  "--has-selected-model": selectedValue.value != null,
}));
</script>

<style>
:host {
  --filter-width: 100%;
  display: block;
  box-sizing: border-box;
}
:host([chrome="bar"]) {
  --filter-width: max-content;
  /*
   * Match the other filter-bar menus' popup width (Provider, Distance share this
   * token). Scoped to the chip chrome so the landing-page version — a
   * separate sp-finder-omnisearch — keeps sizing to its own wide input.
   */
  --sp-comp-popup-width: var(--sp-comp-filter-bar-popup-width, 18.5rem);
}
</style>

<style lang="scss" scoped>
sp-quantum-select {
  --sp-comp-list-item-prepend-margin-right: var(--sp-ref-scale-8, 0.5rem);
  height: 100%;
}

.specialty-filter {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;

  &.--inline {
    display: inline-flex;
    --filter-width: auto;
    align-self: center;
  }

  &.--has-selected-model .activator {
    --sp-comp-text-field-color: var(--sp-sys-color-on-surface);
  }
}

sp-quantum-select,
.activator,
.specialty-filter {
  --sp-comp-button-width: var(--filter-width);
  width: var(--filter-width);
}
</style>
